import React from 'react';
import { X, Check, ArrowRight, Sparkles } from 'lucide-react';
import { TIERS, bulletText, type Tier } from '../pages/pricingData';
import { PLANS, type PlanId } from '../../lib/plans';

const CALENDLY_URL =
  'https://calendly.com/contact-revertai?hide_event_type_details=1&hide_gdpr_banner=1&background_color=020617&text_color=ffffff&primary_color=f59e0b';

/**
 * Résumé des limites — dérivé de `lib/plans.ts` pour les offres réellement ouvertes.
 * Les forfaits « à venir » n'ont pas de quota applicable : on n'affiche rien plutôt
 * que d'inventer un chiffre que le moteur ne fera pas respecter.
 */
function limitsFor(tierId: string): string | null {
  const plan = PLANS[tierId as PlanId];
  if (!plan) return null;
  const { candidates, missionsPerMonth, matchingsPerMonth } = plan.quotas;
  const missions = missionsPerMonth === null ? 'missions illimitées' : `${missionsPerMonth} missions/mois`;
  return `${candidates} candidats · ${missions} · ${matchingsPerMonth} matchings/mois`;
}

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan?: string;
}

function TierCard({ tier, isCurrent }: { tier: Tier; isCurrent: boolean }) {
  const openCalendly = () => {
    if ((window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({ url: CALENDLY_URL });
    } else {
      window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div
      className={`relative flex flex-col rounded-2xl p-5 transition-all ${
        tier.highlight
          ? 'bg-slate-800 border-2 border-amber-500 shadow-[0_0_30px_rgba(245,158,11,0.12)]'
          : isCurrent
            ? 'bg-slate-800/40 border border-slate-700/50 opacity-60'
            : 'bg-slate-800 border border-slate-700 hover:border-slate-600'
      }`}
    >
      {tier.highlight && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-amber-500 text-white text-[10px] font-bold uppercase tracking-wide">
          Recommandé
        </div>
      )}

      {isCurrent && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-slate-700 text-slate-300 text-[10px] font-bold uppercase tracking-wide">
          Plan actuel
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm ${tier.accentBg} ${tier.accent}`}>
          {tier.icon}
        </div>
        <div>
          <p className="text-sm font-bold text-white leading-none">{tier.name}</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{tier.tagline}</p>
        </div>
      </div>

      {/* Price */}
      <div className="mb-3">
        {tier.monthly === null ? (
          <span className="text-xl font-extrabold text-white">Sur devis</span>
        ) : tier.monthly === 0 ? (
          <span className="text-xl font-extrabold text-white">Gratuit</span>
        ) : (
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-extrabold text-white">{tier.monthly} €</span>
            <span className="text-xs text-slate-500">/mois</span>
          </div>
        )}
        {limitsFor(tier.id) && <p className="text-[11px] text-slate-500 mt-0.5">{limitsFor(tier.id)}</p>}
      </div>

      {/* Key features (first 4) */}
      <ul className="space-y-1.5 mb-4 flex-1">
        {tier.bullets.slice(0, 4).map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-xs text-slate-300">
            <Check size={13} className={`shrink-0 mt-0.5 ${isCurrent ? 'text-slate-500' : tier.accent}`} strokeWidth={2.5} />
            <span className="leading-relaxed">{bulletText(b)}</span>
          </li>
        ))}
      </ul>

      {/* CTA */}
      {!isCurrent && (
        <button
          onClick={openCalendly}
          className={`w-full py-2 rounded-xl text-xs font-semibold transition-all flex items-center justify-center gap-1.5 ${
            tier.ctaStyle === 'primary'
              ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/30'
              : 'bg-slate-700 hover:bg-slate-600 text-white border border-slate-600'
          }`}
        >
          {tier.monthly === null ? 'Demander un devis' : `Passer à ${tier.name}`}
          <ArrowRight size={13} />
        </button>
      )}
    </div>
  );
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({ isOpen, onClose, currentPlan = 'beta' }) => {
  if (!isOpen) return null;

  const currentTier = TIERS.find(t => t.id === currentPlan) ?? TIERS[0];
  // Seules les offres réellement souscriptibles sont proposées : proposer « Passer à
  // Sourcer » alors que rien n'est livrable serait un engagement qu'on ne tient pas.
  const upgradeTiers = TIERS.filter(t => t.id !== currentPlan && t.status === 'live');

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-3xl shadow-2xl shadow-black/50 max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between p-6 pb-4 border-b border-slate-800">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-amber-400 text-[10px] font-bold uppercase tracking-wider mb-3">
              <Sparkles size={11} /> Passer au niveau supérieur
            </div>
            <h2 className="text-xl font-bold text-white">Débloquez plus de puissance</h2>
            <p className="text-sm text-slate-400 mt-1">
              Vous êtes sur le plan{' '}
              <span className={`font-semibold ${currentTier.accent}`}>{currentTier.name}</span>.{' '}
              {limitsFor(currentPlan)}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-500 hover:text-white transition-colors p-1 rounded-lg hover:bg-slate-800 ml-4 shrink-0"
          >
            <X size={20} />
          </button>
        </div>

        {/* Cards */}
        <div className={`p-6 grid gap-4 ${upgradeTiers.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' : upgradeTiers.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'}`}>
          {upgradeTiers.map(tier => (
            <TierCard key={tier.id} tier={tier} isCurrent={false} />
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 text-center">
          <p className="text-xs text-slate-500">
            Une question sur le bon forfait ?{' '}
            <button
              onClick={() => {
                if ((window as any).Calendly) {
                  (window as any).Calendly.initPopupWidget({ url: CALENDLY_URL });
                } else {
                  window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
                }
              }}
              className="text-amber-500 hover:text-amber-400 underline underline-offset-2 transition-colors"
            >
              Demandez une démo
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeModal;
